import { app } from '@/main';
import {
  StartGuideCardActiveProgram,
  StartGuideCardActiveProgramResponse,
  StartGuideDiscoverMoreCard,
  StartGuideCardNeedHelp,
  StartGuidePreferences,
  StartGuideTask,
  StartGuideTasksResponse
} from '@/models/start-guide';
import mobileCheck from '@/utils/mobile-check';
import { IBusinessType } from '@/models/business';

import APIService from '../api';
import flagKeys from '@/constants/flagKeys';

export const DISCOVER_MORE_ITEMS_KEY = '@twism:discover-more';
export default class StartGuideService extends APIService {
  updateTask(task: StartGuideTask) {
    return this.put('/admin/business-start-guide/tasks', task);
  }

  updatePreferences(preferences: StartGuidePreferences) {
    return this.put('/admin/business-start-guide/preferences', preferences);
  }

  getTasks() {
    return this.get<StartGuideTasksResponse>(
      '/admin/business-start-guide/tasks'
    );
  }

  getActivePrograms() {
    return this.get<StartGuideCardActiveProgramResponse>(
      '/admin/business-start-guide/active-programs'
    );
  }

  updateActivePrograms(body: { program: StartGuideCardActiveProgram }) {
    return this.put(
      '/admin/business-start-guide/active-programs',
      body.program
    );
  }

  get flags() {
    return app.$store.getters['flags/getFlags'];
  }

  get enableBusinessSocialLinks() {
    return this.flags.includes(flagKeys.businessSocialLinks);
  }

  getDiscoverMore() {
    return new Promise<StartGuideDiscoverMoreCard[]>(resolve => {
      const { business = {} } = app.$store.getters['business/getBusinessInfo'];

      const qtyBusinessLocations =
        business.businessLocations && business.businessLocations.length;
      const qtyBusinessTeamMembers =
        business.teamMembers && business.teamMembers.length;
      const hasSocialLinks =
        this.enableBusinessSocialLinks &&
        (business.facebookLink || business.instagramLink);

      const completeProfileValidation: Record<IBusinessType, boolean> = {
        [IBusinessType.INSTORE]:
          business.website || business.phone || hasSocialLinks,
        [IBusinessType.BOTH]: business.phone || hasSocialLinks,
        [IBusinessType.ONLINE]: business.phone || hasSocialLinks
      };

      const isCompletedProfile = !!completeProfileValidation[
        business.type as IBusinessType
      ];

      const arrDiscorver: StartGuideDiscoverMoreCard[] = [];

      if (!isCompletedProfile) {
        arrDiscorver.push({
          slug: 'tell-us-more',
          title: 'Tell us more about your business',
          description: 'This helps the market know more about your business.',
          link: 'settings/business',
          linkLabel: 'Complete profile',
          dismissed: false
        });
      }

      if (qtyBusinessLocations < 2) {
        arrDiscorver.push({
          slug: 'add-locations',
          title: 'Add more store locations to your Twism account',
          description:
            'If you operate in multiple locations, add every store location where your members can use their Coins.',
          link: 'settings/locations',
          linkLabel: 'Add location',
          dismissed: false
        });
      }

      if (qtyBusinessTeamMembers < 2) {
        arrDiscorver.push({
          slug: 'add-members',
          title: 'Add members to your Twism account',
          description: mobileCheck()
            ? 'You can give other employees access to your Twism account so that they can manage it for you.'
            : 'Give your employees access to your Twism account so that they can manage it for you. For example, an assistant manager, marketing director, or social media manager.',
          link: 'settings/team',
          linkLabel: 'Add member',
          dismissed: false
        });
      }

      setTimeout(() => resolve(arrDiscorver), 200);
    });
  }

  getNeedHelp() {
    return new Promise<StartGuideCardNeedHelp[]>(resolve => {
      const arrNeedHelp = [
        {
          title: 'How will my members receive my Coins?',
          description:
            'After setting up your program, members will receive Coins in Twism app (if you own a physical business) or in your Twism Online Widget (if you run an Online Business) and any member will be able to see the programs you activated.',
          link: 'https://support.twism.com/hc/en-us/articles/4409685432205'
        },
        {
          title: `How will my members use my Coins?`,
          description: `After setting up one of our programs, your store will be displayed in Twism app and any member will be able to see your Offers, Welcome Coins, Gift Coins, and receive Loyalty Coins if they have  a card linked to the app...`,
          link:
            'https://support.twism.com/hc/en-us/articles/4409691251725-When-Consumers-Redeem-their-Rewards-Coins-'
        }
      ];

      const { business = {} } = app.$store.getters['business/getBusinessInfo'];

      if (business.type !== IBusinessType.ONLINE)
        arrNeedHelp.unshift({
          title: `Train staff to promote your Loyalty Rewards Program`,
          description: `Tips on how your staff can inform members about your rewards program.`,
          link: 'https://support.twism.com/hc/en-us/articles/10173322925453'
        });

      setTimeout(() => resolve(arrNeedHelp), 200);
    });
  }
}
